<script setup lang="ts">
import { computed } from "vue";
import { getLastResourceWantToMeet } from "@bc-library/recruitment";
import { useI18n } from "vue-i18n";
import BButton from "@/components/common/bButton.vue";
import ResourceImage from "@/components/resource/resourceImage.vue";
import IcTwotoneDeleteForever from "~icons/ic/twotone-delete-forever";
import MaterialSymbolsCancel from "~icons/material-symbols/cancel";
import MaterialSymbolsCheckCircleOutlineRounded from "~icons/material-symbols/check-circle-outline-rounded";
import MaterialSymbolsPause from "~icons/material-symbols/pause";
import MaterialSymbolsScheduleOutline from "~icons/material-symbols/schedule-outline";
import PhEnvelope from "~icons/ph/envelope";
import { displayLocale } from '@/services/application/displayLocale';
import RejectReasonPill from "@/components/recruitment/screening/RejectReasonPill.vue";
import { useScreeningRejectionCategories } from "@/composables/recruitment/useScreeningRejectionCategories";

const { t } = useI18n();

const emits = defineEmits<{
  updateStatus: [status: number];
  /** « À rejeter » with this reason, or the reason changed on a candidate already « À rejeter » */
  rejectWithReason: [categoryId: string | null];
  openRejectionModal: [];
  openRecontactDate: [];
  openSendMail: [];
}>();

const application = defineModel<any>({ required: true });
const today = new Date();

const standbyDate = computed(() => {
  const log = application.value.logs
    ?.filter((item: { actionId: number }) => item.actionId === 16)
    .pop();
  return log?._created ? new Date(log._created) : null;
});

const isUndecided = computed(() => !application.value.screeningStatus);

const { categories, categoryById } = useScreeningRejectionCategories();
const rejectionReason = computed(() => categoryById(application.value.rejectedCategory));
</script>

<template>
  <div
    class="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-2"
    data-cy="screening-status"
  >
    <template v-if="isUndecided">
      <RejectReasonPill
        v-if="categories.length"
        size="medium"
        button-container-class="min-w-0 flex-1"
        button-class="w-full"
        data-cy="reject-button"
        @select="emits('rejectWithReason', $event)"
      >
        <MaterialSymbolsCancel class="text-danger-500" />
        {{ t("modules.recruitment.screening.candidateCard.reject") }}
      </RejectReasonPill>
      <BButton
        v-else
        size="medium"
        bg="base"
        class="min-w-0 flex-1"
        data-cy="reject-button"
        @click="emits('updateStatus', 1)"
      >
        <MaterialSymbolsCancel class="text-danger-500" />
        {{ t("modules.recruitment.screening.candidateCard.reject") }}
      </BButton>
      <BButton
        size="medium"
        bg="base"
        class="min-w-0 flex-1"
        data-cy="screening-action-contact-later"
        @click="emits('openRecontactDate')"
      >
        <MaterialSymbolsScheduleOutline />
        {{ t("modules.recruitment.screening.candidateCard.contactLater") }}
      </BButton>
      <BButton
        size="medium"
        bg="base"
        class="min-w-0 flex-1"
        data-cy="screening-action-standby"
        @click="emits('updateStatus', 4)"
      >
        <MaterialSymbolsPause />
        {{ t("modules.recruitment.screening.candidateCard.standby") }}
      </BButton>
      <BButton
        size="medium"
        bg="base"
        class="min-w-0 flex-1"
        data-cy="screening-action-meet"
        @click="emits('updateStatus', 3)"
      >
        <MaterialSymbolsCheckCircleOutlineRounded class="text-success-500" />
        {{ t("modules.recruitment.screening.candidateCard.meet") }}
      </BButton>
    </template>

    <template v-else-if="application.screeningStatus === 1">
      <BButton
        size="medium"
        bg="base"
        class="min-w-0 flex-1"
        data-cy="screening-action-cancel-reject"
        @click="emits('updateStatus', 1)"
      >
        <ResourceImage
          disable-link
          popover
          height="h-5"
          width="w-5"
          :resource="getLastResourceWantToMeet(application, 11)"
        />
        {{ t("modules.recruitment.screening.candidateCard.cancelReject") }}
      </BButton>
      <RejectReasonPill
        v-if="categories.length"
        size="medium"
        :selected-id="rejectionReason?._id ?? null"
        button-container-class="min-w-0 flex-1"
        button-class="w-full"
        :title="t('modules.recruitment.screening.rejectReasons.change')"
        data-cy="screening-reject-reason"
        @select="emits('rejectWithReason', $event)"
      >
        <span class="truncate" :class="{ subtitle: !rejectionReason }">
          {{ rejectionReason?.display ?? t("modules.recruitment.screening.rejectReasons.noReason") }}
        </span>
      </RejectReasonPill>
      <BButton
        size="medium"
        bg="warning"
        icon
        :title="t('modules.recruitment.screening.candidateCard.sendMail')"
        data-cy="screening-action-send-mail"
        @click="emits('openSendMail')"
      >
        <PhEnvelope />
      </BButton>
      <BButton
        size="medium"
        bg="error"
        class="min-w-0 flex-1"
        data-cy="confirm-reject-button"
        @click="emits('openRejectionModal')"
      >
        {{ t("modules.recruitment.screening.candidateCard.confirm") }}
        <IcTwotoneDeleteForever />
      </BButton>
    </template>

    <template v-else-if="application.screeningStatus === 2">
      <BButton
        size="medium"
        class="min-w-0 flex-1"
        :bg="application.nextContact < today ? 'success' : 'warning'"
        data-cy="screening-action-contact"
        @click="emits('updateStatus', 2)"
      >
        <ResourceImage
          v-if="application.lastResourceWantToMeet"
          disable-link
          popover
          height="h-5"
          width="w-5"
          :resource="getLastResourceWantToMeet(application, 12)"
        />
        {{ t("modules.recruitment.screening.candidateCard.contact") }}
        <template v-if="application.nextContact">
          {{ application.nextContact.toLocaleDateString(displayLocale) }}
        </template>
      </BButton>
    </template>

    <template v-else-if="application.screeningStatus === 3">
      <BButton
        v-if="!application.tagRecruiter"
        size="medium"
        bg="success"
        class="min-w-0 flex-1"
        data-cy="screening-action-to-meet"
        @click="emits('updateStatus', 3)"
      >
        <ResourceImage
          disable-link
          popover
          height="h-5"
          width="w-5"
          :resource="getLastResourceWantToMeet(application, 10)"
        />
        {{ t("modules.recruitment.screening.candidateCard.toMeet") }}
      </BButton>
      <BButton
        size="medium"
        bg="warning"
        icon
        :title="t('modules.recruitment.screening.candidateCard.sendMail')"
        data-cy="screening-action-send-mail"
        @click="emits('openSendMail')"
      >
        <PhEnvelope />
      </BButton>
    </template>

    <template v-else-if="application.screeningStatus === 4">
      <BButton
        size="medium"
        bg="warning"
        class="min-w-0 flex-1"
        data-cy="screening-action-cancel-standby"
        @click="emits('updateStatus', 4)"
      >
        <ResourceImage
          disable-link
          popover
          height="h-5"
          width="w-5"
          :resource="getLastResourceWantToMeet(application, 16)"
        />
        {{ t("modules.recruitment.screening.candidateCard.cancelStandby") }}
        <template v-if="standbyDate">
          {{ standbyDate.toLocaleDateString(displayLocale) }}
        </template>
      </BButton>
    </template>
  </div>
</template>
